// Minimal cryptographic helpers for the demo.

// Utility: encode/decode base64 for Uint8Array

export function ensureBytes(v: unknown): Uint8Array {
  if (v instanceof Uint8Array) return v;
  if (typeof v === "string") return fromBase64(v);

  throw new Error(
    `ensureBytes: Expected base64 string or Uint8Array, got ${typeof v}`
  );
}

export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// --- AES-GCM helpers ---

export interface AesGcmCiphertext {
  nonce: Uint8Array;      // 12-byte IV
  ciphertext: Uint8Array; // raw bytes
}

export async function aesGcmEncrypt(
  keyBytes: Uint8Array,
  plaintext: Uint8Array,
  associatedData?: Uint8Array
): Promise<AesGcmCiphertext> {
  const subtle = window.crypto.subtle as SubtleCrypto;

  const key = await subtle.importKey(
    "raw",
    keyBytes as BufferSource,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const nonce = window.crypto.getRandomValues(new Uint8Array(12));

    const params: AesGcmParams = {
    name: "AES-GCM",
    iv: nonce as BufferSource,                      
    additionalData: associatedData as BufferSource | undefined,
    };

  const ciphertextBuf = await subtle.encrypt(
    params,
    key,
    plaintext as BufferSource
  );

  return { nonce, ciphertext: new Uint8Array(ciphertextBuf) };
}

export async function aesGcmDecrypt(
  keyBytes: Uint8Array,
  nonce: Uint8Array,
  ciphertext: Uint8Array,
  associatedData?: Uint8Array
): Promise<Uint8Array> {
  const subtle = window.crypto.subtle as SubtleCrypto;

  const key = await subtle.importKey(
    "raw",
    keyBytes as BufferSource,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

    const params: AesGcmParams = {
    name: "AES-GCM",
    iv: nonce as BufferSource,                       
    additionalData: associatedData as BufferSource | undefined,
    };

  const plaintextBuf = await subtle.decrypt(
    params,
    key,
    ciphertext as BufferSource
  );

  return new Uint8Array(plaintextBuf);
}